
import React, { useState, useRef, useEffect } from 'react';
import { COLORS, RADII, SHADOWS, TRANSITIONS } from '../constants/theme';
import { ChevronDown, Check, ChevronRight } from './Icons';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'white' | 'dark' | 'glass';
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  overflowHidden?: boolean;
}

export const Card: React.FC<CardProps> = ({ 
  children, 
  className = '', 
  variant = 'white', 
  onClick,
  overflowHidden = false
}) => {
  const variants = {
    white: 'bg-white border border-gray-200 dark:bg-brand-card-dark dark:border-white/10 shadow-sm',
    dark: 'bg-brand-dark dark:bg-black text-white shadow-xl border dark:border-white/10',
    glass: 'bg-white/80 dark:bg-white/10 backdrop-blur-md border border-white/50 dark:border-white/20',
  };

  return (
    <div 
      onClick={onClick}
      className={`${RADII.standard} ${variants[variant]} ${className} ${overflowHidden ? 'overflow-hidden' : ''} ${onClick ? 'cursor-pointer active:scale-[0.99] transition-transform' : ''}`}
    >
      {children}
    </div>
  );
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  className = '',
  ...props 
}) => {
  const base = `font-black uppercase tracking-[0.2em] ${TRANSITIONS} active:scale-95 flex items-center justify-center whitespace-nowrap`;
  
  const variants = {
    primary: 'bg-brand-dark text-white hover:bg-brand-medium shadow-md dark:bg-brand-medium dark:text-brand-light dark:hover:bg-brand-dark transition-colors',
    secondary: 'bg-white text-brand-dark border border-brand-dark/10 hover:bg-brand-light shadow-md dark:bg-white/10 dark:text-brand-light dark:hover:bg-white/20',
    outline: 'bg-transparent border border-brand-dark/20 text-brand-dark hover:bg-brand-dark hover:text-white dark:border-brand-bright dark:text-brand-bright dark:hover:bg-brand-bright/10',
    ghost: 'bg-transparent text-brand-medium hover:text-brand-dark hover:bg-brand-light/40 dark:text-brand-bright dark:hover:text-brand-light dark:hover:bg-white/10',
  };

  const sizes = {
    sm: 'px-4 py-2 text-[9px] rounded-[6px]',
    md: 'px-6 py-3 text-[10px] rounded-[10px]',
    lg: 'px-8 py-4 text-[11px] rounded-[14px]',
  };

  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  );
};

export const Toggle: React.FC<{ 
  label: string; 
  enabled: boolean; 
  onChange: (v: boolean) => void;
  className?: string;
}> = ({ label, enabled, onChange, className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <button 
      onClick={() => onChange(!enabled)}
      className={`relative w-10 h-5 rounded-full transition-colors duration-300 focus:outline-none ${enabled ? 'bg-brand-medium' : 'bg-gray-300 dark:bg-white/20'}`}
    >
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-300 shadow-sm ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
    {label && <span className="text-xs font-bold text-brand-dark dark:text-brand-bright uppercase tracking-widest">{label}</span>}
  </div>
);

export const Radio: React.FC<{ 
  label: string; 
  checked: boolean; 
  onClick: () => void;
  icon?: React.ReactNode;
}> = ({ label, checked, onClick, icon }) => (
  <button 
    onClick={onClick}
    className="flex items-center gap-2 group focus:outline-none"
  >
    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${checked ? 'border-brand-medium' : 'border-gray-300 dark:border-white/30 group-hover:border-brand-medium'}`}>
      {checked && <div className="w-2 h-2 rounded-full bg-brand-medium animate-in zoom-in-50" />}
    </div>
    <span className={`text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${checked ? 'text-brand-dark dark:text-brand-light' : 'text-gray-500 dark:text-brand-bright/60 group-hover:text-brand-dark dark:group-hover:text-brand-light'}`}>
      {label}
      {icon}
    </span>
  </button>
);

interface CustomSelectProps {
  label: string;
  value: string;
  options: string[];
  onSelect: (val: string) => void;
  placeholder?: string;
  className?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({ label, value, options, onSelect, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative flex flex-col gap-2 ${className}`} ref={containerRef}>
      <label className="text-[11px] font-black uppercase tracking-[0.2em] text-brand-medium dark:text-brand-bright ml-1">{label}</label>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between border rounded-lg px-4 py-3.5 text-xs font-bold transition-all cursor-pointer ${
          isOpen 
            ? 'bg-white border-brand-medium shadow-md dark:bg-white/10 dark:border-brand-bright' 
            : 'bg-white hover:bg-gray-50 border-gray-200 dark:bg-white/5 dark:border-white/10 dark:hover:bg-white/10 shadow-sm'
        } ${value ? 'text-brand-dark dark:text-brand-light' : 'text-gray-400 dark:text-brand-bright/40'}`}
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronDown size={14} className={`text-brand-medium transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} strokeWidth={3} />
      </div>

      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white dark:bg-brand-card-dark border border-gray-200 dark:border-white/10 rounded-lg shadow-xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="bg-brand-dark dark:bg-brand-medium px-4 py-2 text-[10px] font-black text-white uppercase tracking-widest">
            {label}
          </div>
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((option) => (
              <div
                key={option}
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
                className={`px-4 py-3 text-xs font-bold cursor-pointer transition-colors flex items-center justify-between ${
                  value === option 
                    ? 'bg-brand-light text-brand-dark dark:bg-white/20 dark:text-brand-light' 
                    : 'text-brand-dark/80 dark:text-brand-bright/80 hover:bg-brand-light/50 dark:hover:bg-white/5'
                }`}
              >
                {option}
                {value === option && <Check size={12} className="text-brand-medium dark:text-brand-light" strokeWidth={4} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string }> = ({ label, ...props }) => (
  <div className="space-y-1.5 flex-1">
    {label && <label className="text-[11px] font-black uppercase tracking widest text-gray-400 dark:text-brand-bright ml-1">{label}</label>}
    <div className="relative">
      <select 
        {...props}
        className={`w-full border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 text-sm font-bold text-brand-dark dark:text-brand-light focus:outline-none bg-white dark:bg-black/60 focus:bg-white dark:focus:bg-brand-medium/10 focus:ring-2 focus:ring-brand-medium/30 transition-all appearance-none shadow-sm cursor-pointer ${props.className || ''}`}
      >
        {props.children}
      </select>
      <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-medium pointer-events-none" strokeWidth={3} />
    </div>
  </div>
);

interface BreadcrumbItem {
  label: string;
  active?: boolean;
}

interface PageHeaderProps {
  title: string;
  breadcrumbs: BreadcrumbItem[];
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, breadcrumbs, actions }) => (
  <div className="flex flex-col gap-1 mb-8">
    <div className="flex items-center gap-2 text-[11px] font-black text-brand-medium/60 dark:text-brand-bright/60 mb-0.5 uppercase tracking-widest">
      {breadcrumbs.map((crumb, idx) => (
        <React.Fragment key={crumb.label}>
          <span className={idx === breadcrumbs.length - 1 ? 'text-brand-dark font-black dark:text-brand-light' : 'hover:text-brand-dark dark:hover:text-brand-bright transition-colors cursor-default'}>
            {crumb.label}
          </span>
          {idx < breadcrumbs.length - 1 && (
            <ChevronRight size={12} className="text-gray-400 dark:text-brand-bright/30" />
          )}
        </React.Fragment>
      ))}
    </div>
    
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-black text-brand-dark dark:text-brand-light tracking-tight leading-tight">
        {title}
      </h1>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  </div>
);

export const Table: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className="overflow-x-auto custom-scrollbar max-w-full">
    <table className={`w-full text-left border-collapse ${className}`}>
      {children}
    </table>
  </div>
);

export const Thead: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <thead className={`bg-gray-50 dark:bg-black border-b border-gray-200 dark:border-white/10 ${className}`}>
    {children}
  </thead>
);

export const Tbody: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <tbody className={`divide-y divide-gray-100 dark:divide-white/10 ${className}`}>
    {children}
  </tbody>
);

export const Tr: React.FC<{ children: React.ReactNode; className?: string; isHeader?: boolean; onClick?: React.MouseEventHandler<HTMLTableRowElement> }> = ({ children, className = '', isHeader = false, onClick }) => (
  <tr 
    onClick={onClick}
    className={`${!isHeader ? 'hover:bg-brand-light/20 dark:hover:bg-brand-medium/10 transition-colors group' : ''} ${onClick ? 'cursor-pointer' : ''} ${className}`}
  >
    {children}
  </tr>
);

export const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`px-8 py-5 text-[11px] font-black text-brand-dark dark:text-brand-bright/80 uppercase tracking-[0.2em] whitespace-nowrap ${className}`}>
    {children}
  </th>
);

// Fix: Update Td to support standard td attributes like colSpan
export const Td: React.FC<React.TdHTMLAttributes<HTMLTableDataCellElement>> = ({ children, className = '', ...props }) => (
  <td className={`px-8 py-5 text-[13px] font-bold text-brand-dark dark:text-brand-light transition-colors ${className}`} {...props}>
    {children}
  </td>
);

export const Badge: React.FC<{ children: React.ReactNode; className?: string; color?: string }> = ({ 
  children, 
  className = '',
  color
}) => (
  <div 
    className={`w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 shadow-sm border border-black/5 ${className}`}
    style={color ? { backgroundColor: color } : {}}
  >
    {children}
  </div>
);

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input 
    {...props}
    className={`w-full border border-gray-200 dark:border-white/10 bg-white dark:bg-black/60 rounded-lg px-4 py-3 text-sm font-bold text-brand-dark dark:text-brand-light focus:bg-white dark:focus:bg-brand-medium/10 focus:outline-none focus:ring-2 focus:ring-brand-medium/30 transition-all placeholder:text-gray-400 dark:placeholder:text-brand-bright/30 shadow-sm ${props.className || ''}`}
  />
);
